import React, { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { getStudentServices, createService, updateService, deleteService } from "../../lib/api";

export function ServicesTab({ services, setServices, studentId }) {
  const [newService, setNewService] = useState({
    service_provided: "",
    group_size_abbr: "",
    relation_to_system: "",
    minutes_for_x: 30,
    diagnostic_code: "",
    max_per_week: "",
    provider_name: "",
    provider_title: "",
  });

  const [editingService, setEditingService] = useState(null);
  const [editServiceData, setEditServiceData] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await createService({ ...newService, student_id: studentId });
      toast.success("Service added");
      setNewService({
        service_provided: "",
        group_size_abbr: "",
        relation_to_system: "",
        minutes_for_x: 30,
        diagnostic_code: "",
      });
      const servicesRes = await getStudentServices(studentId);
      setServices(servicesRes.data);
    } catch (err) {
      toast.error("Failed to add service");
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setEditServiceData({
      service_provided: service.service_provided,
      group_size_abbr: service.group_size_abbr,
      relation_to_system: service.relation_to_system,
      minutes_for_x: service.minutes_for_x,
      diagnostic_code: service.diagnostic_code,
      student_id: service.student_id,
    });
    setEditDialogOpen(true);
  };

  const handleSaveServiceEdit = async () => {
    try {
      await updateService(editingService.id, editServiceData);
      toast.success("Service updated");
      setEditDialogOpen(false);
      setEditingService(null);
      const servicesRes = await getStudentServices(studentId);
      setServices(servicesRes.data);
    } catch (err) {
      toast.error("Failed to update service");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await deleteService(serviceId);
      toast.success("Service deleted");
      const servicesRes = await getStudentServices(studentId);
      setServices(servicesRes.data);
    } catch (err) {
      toast.error("Failed to delete service");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Add Service</CardTitle>
          <CardDescription>Services for Medicaid billing - configure data source for reporting</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddService} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Service to be Provided</Label>
                <Input
                  value={newService.service_provided}
                  onChange={(e) => setNewService((p) => ({ ...p, service_provided: e.target.value }))}
                  required
                  placeholder="e.g., Special Education Services"
                  data-testid="service-provided-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Group Size Abbreviation</Label>
                <Input
                  value={newService.group_size_abbr}
                  onChange={(e) => setNewService((p) => ({ ...p, group_size_abbr: e.target.value }))}
                  required
                  placeholder="e.g., SM, LG, IND"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Relation to System (Data Source)</Label>
                <Select value={newService.relation_to_system} onValueChange={(v) => setNewService((p) => ({ ...p, relation_to_system: v }))}>
                  <SelectTrigger data-testid="relation-select">
                    <SelectValue placeholder="Select data source..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assessment_english">Assessment English</SelectItem>
                    <SelectItem value="assessment_math">Assessment Math</SelectItem>
                    <SelectItem value="assessment_science">Assessment Science</SelectItem>
                    <SelectItem value="total_morning_assessments">Total of Morning Assessments</SelectItem>
                    <SelectItem value="daily_school_attendance">Daily School Attendance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Minutes for X</Label>
                <Input
                  type="number"
                  value={newService.minutes_for_x}
                  onChange={(e) => setNewService((p) => ({ ...p, minutes_for_x: parseInt(e.target.value) }))}
                  required
                  placeholder="30"
                />
              </div>
              <div className="space-y-2">
                <Label>Diagnostic Code</Label>
                <Input
                  value={newService.diagnostic_code}
                  onChange={(e) => setNewService((p) => ({ ...p, diagnostic_code: e.target.value }))}
                  required
                  placeholder="e.g., F84.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Max Times per Week</Label>
                <Input
                  type="number"
                  value={newService.max_per_week}
                  onChange={(e) => setNewService((p) => ({ ...p, max_per_week: e.target.value ? parseInt(e.target.value) : "" }))}
                  placeholder="e.g., 5"
                  data-testid="max-per-week-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider Name</Label>
                <Input
                  value={newService.provider_name}
                  onChange={(e) => setNewService((p) => ({ ...p, provider_name: e.target.value }))}
                  placeholder="e.g., Jane Smith"
                  data-testid="provider-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider Title</Label>
                <Input
                  value={newService.provider_title}
                  onChange={(e) => setNewService((p) => ({ ...p, provider_title: e.target.value }))}
                  placeholder="e.g., BCBA, SLP"
                  data-testid="provider-title-input"
                />
              </div>
            </div>
            <Button type="submit" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-service-btn">
              Add Service
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Services</CardTitle>
        </CardHeader>
        <CardContent>
          {services.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Service</th>
                    <th className="text-left py-2 px-3">Group</th>
                    <th className="text-left py-2 px-3">Data Source</th>
                    <th className="text-left py-2 px-3">Min</th>
                    <th className="text-left py-2 px-3">Dx Code</th>
                    <th className="text-left py-2 px-3">Max/Wk</th>
                    <th className="text-left py-2 px-3">Provider</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3">{s.service_provided}</td>
                      <td className="py-2 px-3">{s.group_size_abbr}</td>
                      <td className="py-2 px-3">
                        <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          {s.relation_to_system?.replace(/_/g, " ") || "-"}
                        </span>
                      </td>
                      <td className="py-2 px-3">{s.minutes_for_x}</td>
                      <td className="py-2 px-3 font-mono">{s.diagnostic_code}</td>
                      <td className="py-2 px-3">{s.max_per_week || "-"}</td>
                      <td className="py-2 px-3 text-xs">{s.provider_name ? `${s.provider_name}${s.provider_title ? `, ${s.provider_title}` : ''}` : "-"}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditService(s)} data-testid={`edit-service-${i}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                              <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>
                            </svg>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDeleteService(s.id)} data-testid={`delete-service-${i}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                              <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                            </svg>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No services configured</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Service Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Service Provided</Label>
              <Input
                value={editServiceData.service_provided || ""}
                onChange={(e) => setEditServiceData((p) => ({ ...p, service_provided: e.target.value }))}
                placeholder="e.g., Special Education Services"
                data-testid="edit-service-provided-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Group Size Abbreviation</Label>
                <Input
                  value={editServiceData.group_size_abbr || ""}
                  onChange={(e) => setEditServiceData((p) => ({ ...p, group_size_abbr: e.target.value }))}
                  placeholder="e.g., SM, LG, IND"
                  data-testid="edit-group-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Source</Label>
                <Select value={editServiceData.relation_to_system || ""} onValueChange={(v) => setEditServiceData((p) => ({ ...p, relation_to_system: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select data source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assessment_english">Assessment English</SelectItem>
                    <SelectItem value="assessment_math">Assessment Math</SelectItem>
                    <SelectItem value="assessment_science">Assessment Science</SelectItem>
                    <SelectItem value="total_morning_assessments">Total of Morning Assessments</SelectItem>
                    <SelectItem value="daily_school_attendance">Daily School Attendance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minutes for "X"</Label>
                <Input
                  type="number"
                  value={editServiceData.minutes_for_x || ""}
                  onChange={(e) => setEditServiceData((p) => ({ ...p, minutes_for_x: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Diagnostic Code</Label>
                <Input
                  value={editServiceData.diagnostic_code || ""}
                  onChange={(e) => setEditServiceData((p) => ({ ...p, diagnostic_code: e.target.value }))}
                  placeholder="e.g., F84.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Max Times per Week</Label>
                <Input
                  type="number"
                  value={editServiceData.max_per_week || ""}
                  onChange={(e) => setEditServiceData((p) => ({ ...p, max_per_week: e.target.value ? parseInt(e.target.value) : "" }))}
                  placeholder="e.g., 5"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider Name</Label>
                <Input
                  value={editServiceData.provider_name || ""}
                  onChange={(e) => setEditServiceData((p) => ({ ...p, provider_name: e.target.value }))}
                  placeholder="e.g., Jane Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider Title</Label>
                <Input
                  value={editServiceData.provider_title || ""}
                  onChange={(e) => setEditServiceData((p) => ({ ...p, provider_title: e.target.value }))}
                  placeholder="e.g., BCBA, SLP"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveServiceEdit} className="bg-[#00305E] hover:bg-[#00305E]/90">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
